// @spec SHL-STR-050
'use client';

// eslint-disable-next-line no-restricted-imports -- reads `organization.name` for branding display; ID resolution goes through `useOrganizationId` below.
import { useOrganization, useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import {
  fetchOrganizationSettings,
  type OrganizationSettingsResponse,
} from '@/api/organization-settings';
import {
  OrganizationBrandingContext,
  type OrganizationBranding,
} from '@/contexts/OrganizationBrandingContext';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { createApiClient } from '@/lib/api-client';
import { generateColorVariants, isValidHexColor } from '@/lib/color-utils';
import { ApiClientError } from '@/types/api';

export interface BrandingProviderProps {
  children: React.ReactNode;
}

/**
 * Default favicon path to restore when org doesn't have custom favicon.
 */
const DEFAULT_FAVICON = '/favicon.ico';

/**
 * Apply CSS custom properties for branding colors to document root.
 */
function applyBrandingColors(primaryColor: string | null): void {
  const root = document.documentElement;

  if (primaryColor && isValidHexColor(primaryColor)) {
    const variants = generateColorVariants(primaryColor);

    // Set the brand CSS custom properties
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-primary-hover', variants['700'] ?? primaryColor);
    root.style.setProperty('--brand-primary-light', variants['100'] ?? primaryColor);

    // Also set individual shade variants for more granular control
    for (const [shade, color] of Object.entries(variants)) {
      root.style.setProperty(`--brand-primary-${shade}`, color);
    }
  } else {
    // Remove brand overrides, fall back to theme defaults
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-primary-hover');
    root.style.removeProperty('--brand-primary-light');

    // Remove shade variants
    for (const shade of [
      '50',
      '100',
      '200',
      '300',
      '400',
      '500',
      '600',
      '700',
      '800',
      '900',
      '950',
    ]) {
      root.style.removeProperty(`--brand-primary-${shade}`);
    }
  }
}

/**
 * Update the document favicon.
 */
function updateFavicon(faviconUrl: string | null): void {
  // Find existing favicon link or create one
  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.href = faviconUrl ?? DEFAULT_FAVICON;
}

/**
 * Provider component that supplies organization branding data to the application.
 *
 * Features:
 * - Fetches organization settings including branding data
 * - Applies CSS custom properties for primary color (Enterprise)
 * - Updates favicon dynamically (Enterprise)
 * - Re-applies branding when organization changes
 */
export function BrandingProvider({ children }: BrandingProviderProps) {
  const { getToken } = useAuth();
  // Clerk's `useOrganization` is still used here solely to read
  // `organization.name` for the branding display fallback. ID resolution
  // goes through `useOrganizationId` below (Subsidiary Refactor WP0-E / WP4-E).
  const { organization } = useOrganization();

  // Use the canonical `useOrganizationId` hook so the backend-authoritative
  // active-org mapping wins over potentially stale Clerk
  // `publicMetadata.internalId` reads. Returns `undefined` while resolution
  // is in-flight, which we treat as "not loaded" for branding purposes.
  const internalOrgId = useOrganizationId();
  const isOrgLoaded = internalOrgId !== undefined;

  // Fetch organization settings when we have an org ID
  // Returns null gracefully on 403 (non-admin) or 404 (org not found/no settings)
  const settingsQuery = useQuery<OrganizationSettingsResponse | null>({
    queryKey: ['organizationSettings', internalOrgId],
    queryFn: async () => {
      if (!internalOrgId) return null;

      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      try {
        return await fetchOrganizationSettings(apiClient, internalOrgId);
      } catch (error) {
        if (error instanceof ApiClientError && (error.status === 403 || error.status === 404)) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!internalOrgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof ApiClientError && (error.status === 403 || error.status === 404))
        return false;
      return failureCount < 3;
    },
  });

  // Extract branding data from settings
  const settings = settingsQuery.data;
  const branding = settings?.branding;

  // Determine if this is an Enterprise org (has branding customization)
  // For now, any org with branding settings is considered Enterprise
  const isEnterprise = Boolean(branding?.primaryColor ?? branding?.faviconUrl);

  // Build the branding context value
  const brandingValue = React.useMemo<OrganizationBranding>(() => {
    if (!isOrgLoaded) {
      return {
        logoUrl: null,
        name: 'Quantifier',
        primaryColor: null,
        faviconUrl: null,
        isEnterprise: false,
        isLoading: true,
      };
    }

    if (!organization) {
      return {
        logoUrl: null,
        name: 'Quantifier',
        primaryColor: null,
        faviconUrl: null,
        isEnterprise: false,
        isLoading: false,
      };
    }

    const orgName: string = organization.name;

    return {
      // Only use a custom logo from org settings, not Clerk's auto-generated default image
      logoUrl: settings?.logoUrl ?? null,
      name: settings?.name ?? orgName,
      primaryColor: branding?.primaryColor ?? null,
      faviconUrl: branding?.faviconUrl ?? null,
      isEnterprise,
      isLoading: settingsQuery.isLoading,
    };
  }, [isOrgLoaded, organization, settings, branding, isEnterprise, settingsQuery.isLoading]);

  // Apply branding colors when they change
  React.useEffect(() => {
    if (isEnterprise && branding?.primaryColor) {
      applyBrandingColors(branding.primaryColor);
    } else {
      applyBrandingColors(null);
    }

    return () => {
      // Cleanup: remove branding colors when unmounting or org changes
      applyBrandingColors(null);
    };
  }, [isEnterprise, branding?.primaryColor]);

  // Update favicon when it changes (Enterprise only)
  React.useEffect(() => {
    if (isEnterprise && branding?.faviconUrl) {
      updateFavicon(branding.faviconUrl);
    } else {
      updateFavicon(null);
    }

    return () => {
      // Cleanup: restore default favicon
      updateFavicon(null);
    };
  }, [isEnterprise, branding?.faviconUrl]);

  return (
    <OrganizationBrandingContext.Provider value={brandingValue}>
      {children}
    </OrganizationBrandingContext.Provider>
  );
}
